import * as React from 'react';
import { Container, Form, Button } from 'semantic-ui-react';
import ReCAPTCHA from 'react-google-recaptcha';
import * as Sentry from '@sentry/browser';
import { recaptchaSize } from '../../helpers/common';
import { submitFormData } from '../../helpers/form-api';

const recaptchaKey = process.env.GATSBY_GOOGLE_RECAPTCHA_KEY || '';

class FeedbackForm extends React.Component {
  initialFormData = {
    subject: '',
    customer_type: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    project_no_address: '',
    staff_you_dealt_with: '',
    your_comments: '',
  };

  state = {
    error: null,
    success: null,
    submitting: false,
    formData: { ...this.initialFormData },
  };

  recaptchaRef = React.createRef();

  handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    this.setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  };

  handleCaptcha = (value) => {
    this.setState({
      captcha: value,
    });
  };

  handleSelectChange = (event, { name, value }) => {
    this.setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  };

  handleSubmit = async () => {
    try {
      this.setState({ success: null, error: null, submitting: true });

      if (this.state.captcha) {
        const formData = {
          ...this.state.formData,
          captchaResponse: this.state.captcha,
        };
        const response = await submitFormData('feedback', formData);

        if (response.status !== 200) {
          this.setState({
            success: null,
            error: response.message,
            submitting: false,
            captcha: null,
          });
        } else {
          this.setState({
            success: 'Success',
            error: null,
            submitting: false,
            formData: { ...this.initialFormData },
            captcha: null,
          });
        }

        if (this.recaptchaRef.current) {
          this.recaptchaRef.current.reset();
        }
      } else {
        this.setState({
          success: null,
          error: 'Please tick ReCAPTCHA',
          submitting: false,
        });
      }
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);
      this.setState({ error: 'Error', success: null, submitting: false });
    }
  };

  submitText = () => {
    if (this.state.submitting) {
      return 'Submitting...';
    } else if (this.state.success) {
      return 'Submitted';
    }
    return 'Submit';
  };

  render() {
    const {
      subject,
      customer_type,
      first_name,
      last_name,
      email,
      phone,
      project_no_address,
      staff_you_dealt_with,
      your_comments,
    } = this.state.formData;
    const subjectOptions = [
      {
        text: 'General feedback',
        value: 'General feedback',
        key: 'General feedback',
      },
      {
        text: 'Give a compliment',
        value: 'Give a compliment',
        key: 'Give a compliment',
      },
      {
        text: 'Lodge a complaint',
        key: 'Lodge a complaint',
        value: 'Lodge a complaint',
      },
    ];
    const customerTypeOptions = [
      {
        text: 'I am a CitiPower customer',
        key: 'I am a CitiPower customer',
        value: 'I am a CitiPower customer',
      },
      {
        text: 'I am a Powercor customer',
        key: 'I am a Powercor customer',
        value: 'I am a Powercor customer',
      },
      {
        text: 'I am not a customer',
        key: 'I am not a customer',
        value: 'I am not a customer',
      },
      { text: 'I am unsure', key: 'I am unsure', value: 'I am unsure' },
    ];

    return (
      <section>
        <Container>
          <div className="grid-wrapper">
            <div className="form-container">
              <Form onSubmit={this.handleSubmit}>
                <h4>Your feedback</h4>
                <Form.Select
                  label="Subject"
                  required
                  name="subject"
                  value={subject}
                  options={subjectOptions}
                  placeholder="Please choose"
                  onChange={this.handleSelectChange}
                  id="form-subject"
                />

                <Form.Select
                  label="Customer type"
                  required
                  name="customer_type"
                  value={customer_type}
                  placeholder="Please choose"
                  options={customerTypeOptions}
                  onChange={this.handleSelectChange}
                  id="form-customer-type"
                />

                <Form.Input
                  label="First name"
                  required
                  name="first_name"
                  type="text"
                  value={first_name}
                  onChange={this.handleInputChange}
                  id="form-first-name"
                />

                <Form.Input
                  label="Last name"
                  required
                  type="text"
                  name="last_name"
                  value={last_name}
                  onChange={this.handleInputChange}
                  id="form-last-name"
                />

                <Form.Input
                  label="Email address"
                  required
                  type="email"
                  name="email"
                  value={email}
                  onChange={this.handleInputChange}
                  id="form-email"
                />

                <Form.Input
                  label="Phone number"
                  required
                  type="phone"
                  name="phone"
                  value={phone}
                  onChange={this.handleInputChange}
                  id="form-phone"
                />

                <Form.Input
                  label="Project No./Address"
                  type="text"
                  name="project_no_address"
                  value={project_no_address}
                  onChange={this.handleInputChange}
                  id="form-project-no-address"
                />

                <Form.Input
                  label="Staff you dealt with"
                  type="text"
                  name="staff_you_dealt_with"
                  value={staff_you_dealt_with}
                  onChange={this.handleInputChange}
                  id="form-staff-dealt-with"
                />

                <Form.TextArea
                  label="Your comments"
                  required
                  type="text"
                  name="your_comments"
                  value={your_comments}
                  onChange={this.handleInputChange}
                  id="form-your-comments"
                />

                <ReCAPTCHA
                  ref={this.recaptchaRef}
                  sitekey={recaptchaKey}
                  onChange={this.handleCaptcha}
                  size={recaptchaSize()}
                />

                <Button
                  className="form--submit-btn"
                  disabled={this.state.submitting}
                  loading={this.state.submitting}
                >
                  {this.submitText()}
                </Button>

                {this.state.success ? (
                  <div className="success-message">{this.state.success}</div>
                ) : null}
                {this.state.error ? (
                  <div className="error-message">{this.state.error}</div>
                ) : null}
              </Form>
            </div>
          </div>
        </Container>
      </section>
    );
  }
}

export default FeedbackForm;
